/**
 * The Levies — where your men come from, what each province owes, and what
 * raising a host costs you next year.
 *
 * > *"non of the comabt changes where implemented, it should work the same as
 * > eu5 levies, on the map"*
 *
 * The mechanism has been there the whole time and that is exactly the problem
 * this screen exists for. Provinces carry a `levy`; `doIncome` banks
 * `levy × estateLevyScale` into `nation.manpower` every month; `tryRaiseArmy`
 * spends it. A player could see one figure of all that — "340 banked · +58 a
 * month" on the resource strip — and had no way to ask where the fifty-eight
 * came from, which province was sending nothing because there were tents
 * outside its walls, or how many months of countryside a nine-hundred-point
 * host actually costs. The simulation needed nothing. The *reading* of it was
 * missing entirely.
 *
 * Rules it keeps, which are `manage.ts`'s rules because it is that file's
 * sibling:
 *
 *   • **It writes nothing.** The one control on it posts `Cmd.CampaignRaiseArmy`
 *     — the command the province panel has always used — and waits for the next
 *     frame to tell it what happened.
 *   • **It computes nothing.** Every number comes out of
 *     `sim/campaign/levies.ts`, which in turn never re-derives a levy: it reads
 *     `holdingIncome` and `estateLevyScale`, the two functions the month itself
 *     uses. A screen with its own arithmetic is a screen that will one day
 *     disagree with the month, and this project has that bug written up three
 *     times already.
 *   • **The map is the other half of it.** "Show these on the chart" is a
 *     button rather than a second view, because the thing a player wants after
 *     reading a list of provinces is to see them *in place* — see `levyLayer`
 *     in `worldmap.ts`.
 */

import { setHtml } from './dom.ts';
import { C, toCss } from '../data/palette.ts';
import { Cmd, cmd, type Command } from '../sim/commands.ts';
import type { World } from '../sim/world.ts';
import { provinceName } from '../sim/campaign/state.ts';
import {
  LEVY_DRAG_WORDS,
  LevyDrag,
  levyReport,
  monthsUntilMuster,
  musterDrain,
  type LevyReport,
} from '../sim/campaign/levies.ts';
import { maxAffordablePoints } from '../sim/campaign/armies.ts';
import { MAX_ARMY_POINTS, MIN_ARMY_POINTS, type CampaignState } from '../sim/campaign/types.ts';
import { tierOf } from '../sim/campaign/holdings.ts';

export interface LevyCallbacks {
  /**
   * An order for the realm.
   *
   * Named for the realm rather than "the queue" for the reason `manage.ts`
   * spells out: commands are routed to whichever world is on screen, and a
   * muster is the crown's business even when the player is standing in a town.
   */
  onRealmCommand(c: Command): void;
  /** Put a province on the chart and close this screen. */
  onLookAt(province: number): void;
  /** Draw the levy marks on the chart, and close this screen to show them. */
  onShowMarks(): void;
}

function esc(s: string): string {
  return s.replace(
    /[&<>"]/g,
    (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[ch] as string,
  );
}

/**
 * A span of months, said as a *cost* rather than as a date.
 *
 * The strategic layer's own `months()` answers "this month" for zero, which is
 * right for an arrival and wrong for a bill: "a warband costs this month of
 * levy" is not a sentence. A muster that costs a fraction of a month costs
 * *under a month*, and the number a player is deciding on is how many months of
 * countryside they are spending.
 */
function levyMonths(n: number): string {
  if (!Number.isFinite(n)) return 'never';
  if (n <= 0) return 'nothing';
  if (n < 0.9) return 'under a month';
  const m = Math.round(n);
  return `${m} month${m === 1 ? '' : 's'}`;
}

/** A wait, said as a wait. Zero here really does mean now. */
function waitMonths(n: number): string {
  if (!Number.isFinite(n)) return 'never';
  if (n <= 0) return 'now';
  if (n < 0.9) return 'within the month';
  const m = Math.round(n);
  return `${m} month${m === 1 ? '' : 's'}`;
}

/**
 * The three sizes of muster the screen prices.
 *
 * Fractions of the ceiling rather than round numbers, so the row means the same
 * thing if `MAX_ARMY_POINTS` ever moves — and the smallest is the floor itself,
 * because a host below `MIN_ARMY_POINTS` is not a host the command will raise.
 */
const MUSTER_SIZES: ReadonlyArray<{ points: number; word: string }> = [
  { points: MIN_ARMY_POINTS, word: 'A warband' },
  { points: Math.round(MAX_ARMY_POINTS * 0.5), word: 'A host' },
  { points: MAX_ARMY_POINTS, word: 'The full muster' },
];

export class LevyBoard {
  private el: HTMLElement;
  private visible = false;
  /**
   * Cheap change detector, exactly as the world map's is.
   *
   * This screen is driven from the frame loop over a realm that moves on its
   * own, and its markup is a table of every province you hold. Rebuilding that
   * sixty times a second to change nothing would drop the hover card and eat a
   * click that straddled a repaint — the two faults the world map's own pass
   * found in a screenshot rather than in the code.
   */
  private sig = '';
  private state: CampaignState | null = null;

  constructor(private cb: LevyCallbacks) {
    injectLevyStyle();
    this.el = document.createElement('div');
    this.el.id = 'levies';
    this.el.className = 'gone';
    (document.getElementById('ui-root') as HTMLElement).appendChild(this.el);
    /**
     * One delegated listener on the root, and never a handler per row.
     *
     * The markup is replaced wholesale whenever the realm moves; per-element
     * handlers would be rebuilt with it, and a click that landed between the
     * two would go nowhere.
     */
    this.el.addEventListener('click', (ev) => {
      const t = (ev.target as Element | null)?.closest?.('[data-act]') as HTMLElement | null;
      if (!t || !this.state) return;
      this.act(this.state, t);
    });
  }

  get isOpen(): boolean {
    return this.visible;
  }

  show(): void {
    this.visible = true;
    this.sig = '';
    this.el.classList.remove('gone');
  }

  hide(): void {
    this.visible = false;
    this.el.classList.add('gone');
  }

  toggle(): void {
    if (this.visible) this.hide();
    else this.show();
  }

  /**
   * Called every frame with the realm world, or null.
   *
   * Null closes it: a screen about a crown's levies has nothing to say once
   * there is no crown, and leaving it up over the title screen is the class of
   * bug `warcall.ts` was written to end.
   */
  update(realm: World | null): void {
    if (!this.visible) return;
    const c = realm?.campaign;
    if (!c) {
      this.hide();
      return;
    }
    this.state = c;
    const rep = levyReport(c, c.playerNation);
    const sig = [
      c.turn,
      Math.round(rep.banked),
      Math.round(rep.monthly * 10),
      Math.round(rep.estateScale * 100),
      rep.underArms,
      rep.sources.map((s) => `${s.province}.${Math.round(s.levy * 10)}.${s.drag}`).join(''),
      Math.round(c.nations[c.playerNation]?.treasury.gold ?? 0),
    ].join('|');
    if (sig === this.sig) return;
    this.sig = sig;
    setHtml(this.el, this.markup(c, rep));
  }

  private act(c: CampaignState, el: HTMLElement): void {
    switch (el.dataset.act) {
      case 'close':
        this.hide();
        return;
      case 'marks':
        this.cb.onShowMarks();
        this.hide();
        return;
      case 'look':
        this.cb.onLookAt(Number(el.dataset.prov));
        this.hide();
        return;
      case 'raise': {
        /**
         * The same sizing rule the province panel uses, and for the same
         * reason: a button that quotes a price the till does not charge and
         * then does nothing when pressed is worse than no button.
         */
        const points = maxAffordablePoints(c, c.playerNation);
        if (points < MIN_ARMY_POINTS) return;
        this.cb.onRealmCommand(cmd(Cmd.CampaignRaiseArmy, Number(el.dataset.prov), points));
        // Force the next frame to redraw: the muster lands on the command
        // rather than at a council, so `c.turn` will not have moved.
        this.sig = '';
        return;
      }
      default:
        return;
    }
  }

  private markup(c: CampaignState, rep: LevyReport): string {
    const most = maxAffordablePoints(c, c.playerNation);
    const bar = rep.sources.length > 0 ? Math.max(...rep.sources.map((s) => s.levy)) : 1;

    const rows = rep.sources
      .map((s) => {
        const word = LEVY_DRAG_WORDS[s.drag] ?? '';
        const share = rep.gross > 0 ? (s.levy / rep.gross) * 100 : 0;
        const width = bar > 0 ? Math.max(2, (s.levy / bar) * 100) : 2;
        const canRaise = most >= MIN_ARMY_POINTS && c.provinces[s.province].siegeBy < 0;
        return `<div class="lv-row${s.drag !== LevyDrag.None ? ' dragged' : ''}">
          <span class="lv-name" data-act="look" data-prov="${s.province}"
            title="Show it on the chart">${esc(provinceName(s.province))}</span>
          <span class="lv-tier">${esc(s.tierWord)} · ${tierOf(c, s.province)}</span>
          <span class="lv-bar"><i style="width:${width.toFixed(1)}%"></i></span>
          <span class="lv-men">${s.levy.toFixed(1)}<em>${share.toFixed(0)}%</em></span>
          <span class="lv-why">${
            word
              ? `<b class="lv-drag">${esc(word)}</b> — sends ${(s.health * 100).toFixed(0)}% of ${s.settled.toFixed(1)}`
              : '<span class="lv-ok">in good order</span>'
          }</span>
          <span class="lv-do">${
            canRaise
              ? `<span class="lv-btn" data-act="raise" data-prov="${s.province}">Raise here</span>`
              : ''
          }</span>
        </div>`;
      })
      .join('');

    /**
     * What a muster costs, in months of countryside.
     *
     * This is the number the item actually asked for. "Nine hundred points,
     * two hundred and seventy gold" is a sentence about today; "and eleven
     * months of every levy in the realm" is the sentence a player needs before
     * pressing it, and nothing on this screen or any other was saying it.
     */
    const musterRows = MUSTER_SIZES.map((m) => {
      const d = musterDrain(c, c.playerNation, m.points);
      const wait = monthsUntilMuster(c, c.playerNation, m.points);
      return `<div class="lv-cost${d.affordable ? '' : ' short'}">
        <span>${esc(m.word)}<em>${d.points} pts</em></span>
        <b>${d.models} men</b>
        <span class="lv-dim">${Math.round(d.gold)}g${d.iron > 0 ? ` · ${Math.round(d.iron)} iron` : ''}</span>
        <span class="lv-dim">${levyMonths(d.months)} of levy</span>
        <span class="${d.affordable ? 'lv-ok' : 'lv-short'}">${
          d.affordable
            ? 'you can raise it today'
            : d.banked < 0
              ? `${Math.ceil(-d.banked)} men short — ${waitMonths(wait)}`
              : 'the treasury is short'
        }</span>
      </div>`;
    }).join('');

    const hostsWord = rep.onStrike
      ? 'The Hosts have downed tools. Nothing is coming in at all.'
      : rep.estateScale >= 1.15
        ? 'The Hosts are with you, and the countryside sends more than it owes.'
        : rep.estateScale <= 0.85
          ? 'The Hosts are sour. Fewer men come in than the ground owes you.'
          : 'The Hosts are neither pleased nor angry.';

    return `
      <div class="lv-sheet">
        <div class="lv-head">
          <div>
            <h2>The Levies</h2>
            <p>Every man your ground sends you, and what spending them costs.</p>
          </div>
          <div class="lv-heading-acts">
            <span class="lv-btn" data-act="marks">Show these on the chart</span>
            <span class="lv-btn ghost" data-act="close">Close <i>Esc</i></span>
          </div>
        </div>

        <div class="lv-tops">
          <div class="lv-top">
            <span>In the bank</span><b>${Math.round(rep.banked)} men</b>
            <em>${rep.underArms} already under arms</em>
          </div>
          <div class="lv-top">
            <span>The ground sends</span><b>${rep.gross.toFixed(1)} a month</b>
            <em>${rep.sources.length} province${rep.sources.length === 1 ? '' : 's'}</em>
          </div>
          <div class="lv-top${rep.onStrike ? ' bad' : ''}">
            <span>The Hosts take their view</span>
            <b>×${rep.estateScale.toFixed(2)}</b>
            <em>standing ${Math.round(rep.hostsStanding)}</em>
          </div>
          <div class="lv-top strong">
            <span>Reaches the bank</span><b>+${rep.monthly.toFixed(1)} a month</b>
            <em>${esc(hostsWord)}</em>
          </div>
        </div>

        <div class="lv-sub">What a muster costs</div>
        <div class="lv-costs">${musterRows}</div>

        <div class="lv-sub">Where the men come from</div>
        <div class="lv-rows">${
          rows ||
          '<div class="lv-empty">You hold no ground. Nothing is coming in and nothing will.</div>'
        }</div>
      </div>`;
  }
}

function injectLevyStyle(): void {
  if (document.getElementById('levies-style')) return;
  const s = document.createElement('style');
  s.id = 'levies-style';
  s.textContent = `
    #levies { position: absolute; inset: 0; z-index: 86; display: flex;
      align-items: flex-start; justify-content: center; overflow: auto;
      background: rgba(9,7,6,0.72); backdrop-filter: blur(2px); padding: 28px 16px; }
    #levies.gone { display: none; }
    #levies .lv-sheet { width: min(1080px, 100%); background: ${toCss(C.ashDark)};
      border: 1px solid ${toCss(C.ashLight)}; border-radius: 8px; padding: 18px 20px 24px;
      box-shadow: 0 18px 60px rgba(0,0,0,0.55); color: ${toCss(C.bone)};
      font-family: 'Trebuchet MS', 'Gill Sans', sans-serif; }
    #levies h2 { margin: 0; font-size: 22px; letter-spacing: 0.06em;
      text-transform: uppercase; color: ${toCss(C.gold)}; }
    #levies p { margin: 3px 0 0; font-size: 12.5px; opacity: 0.75; }
    #levies .lv-head { display: flex; justify-content: space-between; align-items: flex-start;
      gap: 12px; border-bottom: 1px solid ${toCss(C.ashLight)}; padding-bottom: 12px; }
    #levies .lv-heading-acts { display: flex; gap: 8px; flex-wrap: wrap; }
    #levies .lv-btn { cursor: pointer; user-select: none; font-size: 12px; padding: 5px 10px;
      border: 1px solid ${toCss(C.gold)}; border-radius: 4px; color: ${toCss(C.gold)};
      background: rgba(230,207,146,0.06); white-space: nowrap; }
    #levies .lv-btn:hover { background: rgba(230,207,146,0.18); }
    #levies .lv-btn i { font-style: normal; opacity: 0.6; margin-left: 5px; }
    #levies .lv-btn.ghost { border-color: ${toCss(C.ashLight)}; color: ${toCss(C.bone)}; }
    #levies .lv-tops { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px;
      margin: 14px 0 4px; }
    #levies .lv-top { border: 1px solid ${toCss(C.ashLight)}; border-radius: 5px;
      padding: 8px 10px; background: rgba(0,0,0,0.16); }
    #levies .lv-top span { display: block; font-size: 10.5px; letter-spacing: 0.08em;
      text-transform: uppercase; opacity: 0.62; }
    #levies .lv-top b { display: block; font-size: 19px; margin-top: 2px; }
    #levies .lv-top em { display: block; font-style: normal; font-size: 11px; opacity: 0.62;
      margin-top: 3px; line-height: 1.3; }
    #levies .lv-top.strong { border-color: ${toCss(C.gold)}; }
    #levies .lv-top.strong b { color: ${toCss(C.gold)}; }
    #levies .lv-top.bad b { color: ${toCss(C.oxbloodLight)}; }
    #levies .lv-sub { margin: 18px 0 6px; font-size: 11px; letter-spacing: 0.14em;
      text-transform: uppercase; opacity: 0.6; }
    #levies .lv-cost { display: grid; grid-template-columns: 1.4fr 0.7fr 0.9fr 1fr 1.6fr;
      gap: 8px; align-items: baseline; padding: 6px 8px; border-radius: 4px;
      border: 1px solid transparent; font-size: 12.5px; }
    #levies .lv-cost:nth-child(odd) { background: rgba(0,0,0,0.14); }
    #levies .lv-cost.short { border-color: rgba(156,51,54,0.5); }
    #levies .lv-cost em { font-style: normal; opacity: 0.55; margin-left: 6px; font-size: 11px; }
    #levies .lv-dim { opacity: 0.66; }
    #levies .lv-ok { color: ${toCss(C.goldPale)}; opacity: 0.85; }
    #levies .lv-short { color: ${toCss(C.oxbloodLight)}; }
    #levies .lv-row { display: grid;
      grid-template-columns: 1.3fr 0.9fr 1.5fr 0.7fr 1.7fr 0.8fr;
      gap: 8px; align-items: center; padding: 5px 8px; border-radius: 4px; font-size: 12.5px; }
    #levies .lv-row:nth-child(odd) { background: rgba(0,0,0,0.14); }
    #levies .lv-row.dragged .lv-men { color: ${toCss(C.oxbloodLight)}; }
    #levies .lv-name { cursor: pointer; color: ${toCss(C.goldPale)}; }
    #levies .lv-name:hover { text-decoration: underline; }
    #levies .lv-tier { opacity: 0.6; font-size: 11px; }
    #levies .lv-bar { display: block; height: 8px; background: rgba(0,0,0,0.35);
      border-radius: 2px; overflow: hidden; }
    #levies .lv-bar i { display: block; height: 100%; background: ${toCss(C.gold)}; opacity: 0.7; }
    #levies .lv-men { text-align: right; font-size: 14px; }
    #levies .lv-men em { display: block; font-style: normal; font-size: 10px; opacity: 0.55; }
    #levies .lv-why { font-size: 11.5px; opacity: 0.85; }
    #levies .lv-drag { color: ${toCss(C.oxbloodLight)}; text-transform: uppercase;
      font-size: 10.5px; letter-spacing: 0.06em; }
    #levies .lv-do { text-align: right; }
    #levies .lv-empty { opacity: 0.6; font-size: 12.5px; padding: 8px; }
    @media (max-width: 820px) {
      #levies { padding: 10px 6px; }
      #levies .lv-tops { grid-template-columns: repeat(2, 1fr); }
      #levies .lv-row { grid-template-columns: 1.2fr 1fr 0.9fr; }
      #levies .lv-bar, #levies .lv-why, #levies .lv-do { display: none; }
      #levies .lv-cost { grid-template-columns: 1.2fr 0.8fr 1fr; }
      #levies .lv-cost .lv-dim:last-of-type, #levies .lv-cost span:last-child { display: none; }
    }`;
  document.head.appendChild(s);
}
